import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { createPortal } from 'react-dom';
import { apiService } from '@/lib/api';
import { useModalLayer, LayerDepthContext } from '@/lib/layerManager';
import { useBackNavigationLayer } from '@/lib/navigation';
import type { StripePaymentDTO, StripePaymentStatus } from '@/types';
import { X, ExternalLink, CreditCard, ArrowDownCircle, User, RotateCcw, AlertCircle, CheckCircle2, FileText, RefreshCw } from 'lucide-react';
import { useCompanyTimezone } from '@/hooks/useGlobalCompany';
import { formatInstantInCompanyTimeZone } from '@/lib/dateUtils';

interface PaymentDetailModalProps {
    payment: StripePaymentDTO;
    onClose: () => void;
    /** Called after a successful refund so the parent can refresh the payment list */
    onRefunded?: () => void;
}

function formatCurrency(amount: number): string {
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 2,
    }).format(amount);
}

function getStatusBadge(status: StripePaymentStatus) {
    const styles: Record<StripePaymentStatus, { bg: string; text: string }> = {
        Processing: { bg: 'bg-blue-100', text: 'text-blue-700' },
        Succeeded: { bg: 'bg-green-100', text: 'text-green-700' },
        Failed: { bg: 'bg-red-100', text: 'text-red-700' },
        Refunded: { bg: 'bg-gray-100', text: 'text-gray-700' },
        PartiallyRefunded: { bg: 'bg-orange-100', text: 'text-orange-700' },
    };
    const style = styles[status] || styles.Processing;
    return (
        <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${style.bg} ${style.text}`}>
            {status.replace(/([A-Z])/g, ' $1').trim()}
        </span>
    );
}

const canRefund = (status: StripePaymentStatus) =>
    status === 'Succeeded' || status === 'PartiallyRefunded';

export function PaymentDetailModal({ payment, onClose, onRefunded }: PaymentDetailModalProps) {
    const queryClient = useQueryClient();
    const companyTz = useCompanyTimezone();

    // Refund form state
    const [showRefundForm, setShowRefundForm] = useState(false);
    const [refundType, setRefundType] = useState<'full' | 'partial'>('full');
    const [customAmount, setCustomAmount] = useState('');
    const [reason, setReason] = useState('');
    const [refundSuccess, setRefundSuccess] = useState(false);
    const layer = useModalLayer({
        enabled: !showRefundForm,
        onClose,
        historyState: { paymentDetailsModal: true },
    });
    const handleRefundFormClose = useBackNavigationLayer({
        enabled: showRefundForm,
        onBack: () => {
            setShowRefundForm(false);
            resetRefundError();
        },
        state: { paymentRefundForm: true },
    });

    const remainingAmount = payment.amount - payment.refunded_amount;

    // Fee refresh mutation (for delayed fees from Link/Apple Pay)
    const { mutate: refreshFee, isPending: isRefreshingFee } = useMutation({
        mutationFn: () => apiService.refreshPaymentFee(payment.id),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['paymentTransactions'] });
            onRefunded?.(); // trigger parent refresh too
        },
    });

    // Fetch Stripe provider status — same query key as TakePaymentModal, so this is cache-free
    const { data: providerStatus } = useQuery({
        queryKey: ['paymentProviderStatus'],
        queryFn: () => apiService.getPaymentProviderStatus(),
        staleTime: 5 * 60 * 1000,
    });
    const isStripeReady = (providerStatus?.connected ?? false) && (providerStatus?.charges_enabled ?? false);

    // Fetch refunds (enabled once a refund exists or after a successful refund)
    const { data: refunds, isLoading: refundsLoading } = useQuery({
        queryKey: ['paymentRefunds', payment.id],
        queryFn: () => apiService.getPaymentRefunds(payment.id),
        enabled: payment.refunded_amount > 0 || refundSuccess,
    });

    // Refund mutation
    const { mutate: submitRefund, isPending: isRefunding, error: refundError, reset: resetRefundError } = useMutation({
        mutationFn: () => {
            const amountCents = refundType === 'full'
                ? undefined // backend defaults to full remaining
                : Math.round(parseFloat(customAmount) * 100);

            return apiService.refundPayment(payment.id, {
                amount_in_cents: amountCents,
                reason: reason.trim() || undefined,
            });
        },
        onSuccess: () => {
            setRefundSuccess(true);
            setShowRefundForm(false);
            setCustomAmount('');
            setReason('');
            // Invalidate refunds list so it re-fetches
            queryClient.invalidateQueries({ queryKey: ['paymentRefunds', payment.id] });
            // Let parent refresh the payments list
            onRefunded?.();
        },
    });

    const handleRefundAmountChange = (value: string) => {
        // Only allow valid positive dollar amounts
        if (/^\d*\.?\d{0,2}$/.test(value)) {
            setCustomAmount(value);
        }
    };

    const partialAmountValid =
        refundType === 'full' ||
        (parseFloat(customAmount) > 0 && parseFloat(customAmount) <= remainingAmount);

    const stripePaymentUrl = payment.stripe_payment_intent_id
        ? `https://dashboard.stripe.com/payments/${payment.stripe_payment_intent_id}`
        : null;

    return createPortal(
        <LayerDepthContext.Provider value={layer.depth}>
        <div
            className="fixed inset-0 bg-black/50 flex flex-col justify-end overflow-hidden sm:justify-center sm:items-center sm:overflow-y-auto sm:p-4 keyboard-aware-overlay"
            style={layer.style}
            onClick={layer.onClose}
        >
            <div
                className="relative flex w-full max-w-lg flex-col overflow-hidden rounded-t-2xl bg-white shadow-xl sm:rounded-2xl max-h-[min(92dvh,calc(100vh-var(--safe-area-inset-bottom)-0.5rem))] sm:max-h-[90vh] keyboard-constrained-modal"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="flex shrink-0 justify-center pt-2 pb-1 sm:hidden" aria-hidden>
                    <div className="h-1 w-10 rounded-full bg-gray-300" />
                </div>

                {/* Header */}
                <div className="shrink-0 border-b border-gray-200 px-4 pb-3 pt-1 sm:p-6">
                    <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0 flex-1">
                            <div className="flex flex-wrap items-center gap-2">
                                <h2 className="text-lg font-bold text-gray-900 sm:text-xl">Payment Details</h2>
                                {getStatusBadge(payment.status)}
                            </div>
                            <p className="text-sm text-gray-500 mt-1">
                                {formatInstantInCompanyTimeZone(payment.created_at, companyTz)}
                            </p>
                        </div>
                        <button
                            onClick={layer.onClose}
                            className="-mr-1 shrink-0 p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                            aria-label="Close"
                        >
                            <X className="h-5 w-5" />
                        </button>
                    </div>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto overscroll-contain px-4 py-4 space-y-4 sm:p-6 sm:space-y-5">
                    {/* Amount Summary */}
                    <div className="bg-gray-50 rounded-xl p-3 sm:p-4 space-y-2 sm:space-y-3">
                        <div className="flex items-center justify-between">
                            <span className="text-gray-600 text-sm sm:text-base">Gross Amount</span>
                            <span className="text-lg font-bold text-gray-900">{formatCurrency(payment.amount)}</span>
                        </div>
                        <div className="flex items-center justify-between text-sm">
                            <span className="text-gray-500">Stripe Fee</span>
                            {payment.stripe_fee != null ? (
                                <span className="text-gray-600">- {formatCurrency(payment.stripe_fee)}</span>
                            ) : (
                                <button
                                    onClick={() => refreshFee()}
                                    disabled={isRefreshingFee}
                                    className="inline-flex items-center gap-1.5 text-gray-400 hover:text-primary-600 transition-colors"
                                    title="Fee pending — click to refresh"
                                >
                                    <span className="text-xs">Pending</span>
                                    <RefreshCw className={`h-3 w-3 ${isRefreshingFee ? 'animate-spin' : ''}`} />
                                </button>
                            )}
                        </div>
                        {payment.refunded_amount > 0 && (
                            <div className="flex items-center justify-between text-sm">
                                <span className="text-gray-500">Refunded</span>
                                <span className="text-red-600 font-medium">- {formatCurrency(payment.refunded_amount)}</span>
                            </div>
                        )}
                        <div className="border-t border-gray-200 pt-2 sm:pt-3 flex items-center justify-between">
                            <span className="font-medium text-gray-700 text-sm sm:text-base">Net Amount</span>
                            <span className="text-lg font-bold text-green-600">
                                {payment.net_amount_after_fees != null ? formatCurrency(payment.net_amount_after_fees) : '—'}
                            </span>
                        </div>
                    </div>

                    {/* Failure Reason */}
                    {payment.failure_reason && (
                        <div className="bg-red-50 border border-red-200 rounded-lg p-3 sm:p-4">
                            <p className="text-sm font-medium text-red-800">Payment Failed</p>
                            <p className="text-sm text-red-600 mt-1">{payment.failure_reason}</p>
                        </div>
                    )}

                    {/* Refund Success Banner */}
                    {refundSuccess && (
                        <div className="flex items-center gap-3 bg-green-50 border border-green-200 rounded-lg p-3 sm:p-4">
                            <CheckCircle2 className="h-5 w-5 text-green-600 shrink-0" />
                            <p className="text-sm text-green-800 font-medium">Refund submitted successfully.</p>
                        </div>
                    )}

                    {/* Refunds list — shown early so status is visible without scrolling */}
                    {(payment.refunded_amount > 0 || refundSuccess) && (
                        <div>
                            <h3 className="font-medium text-gray-900 mb-2 flex items-center gap-2 text-sm sm:text-base">
                                <ArrowDownCircle className="h-4 w-4 text-red-500" />
                                Refunds
                            </h3>
                            {refundsLoading ? (
                                <div className="animate-pulse space-y-2">
                                    <div className="h-10 bg-gray-100 rounded" />
                                </div>
                            ) : refunds && refunds.length > 0 ? (
                                <div className="space-y-2">
                                    {refunds.map((refund) => (
                                        <div
                                            key={refund.id}
                                            className="flex items-center justify-between gap-3 p-3 bg-red-50 rounded-lg"
                                        >
                                            <div className="min-w-0">
                                                <p className="text-sm font-medium text-gray-900">
                                                    {formatCurrency(refund.amount)}
                                                </p>
                                                <p className="text-xs text-gray-500 break-words">
                                                    {formatInstantInCompanyTimeZone(refund.created_at, companyTz)}
                                                    {refund.reason && ` • ${refund.reason}`}
                                                </p>
                                            </div>
                                            <span className="shrink-0 text-xs px-2 py-1 bg-white rounded font-medium text-gray-700">
                                                {refund.status}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <p className="text-sm text-gray-500">No refund details available</p>
                            )}
                        </div>
                    )}

                    {/* Inline Refund Form */}
                    {showRefundForm && (
                        <div className="border border-orange-200 bg-orange-50 rounded-xl p-4 space-y-4">
                            <h3 className="font-medium text-gray-900 flex items-center gap-2">
                                <RotateCcw className="h-4 w-4 text-orange-500" />
                                Issue Refund
                            </h3>

                            {/* Full / Partial toggle */}
                            <div className="flex gap-2">
                                <button
                                    onClick={() => setRefundType('full')}
                                    className={`flex-1 py-2 text-sm font-medium rounded-lg border transition-colors ${refundType === 'full'
                                        ? 'bg-white border-orange-400 text-orange-700 shadow-sm'
                                        : 'border-gray-200 text-gray-600 hover:border-orange-200'}`}
                                >
                                    Full ({formatCurrency(remainingAmount)})
                                </button>
                                <button
                                    onClick={() => setRefundType('partial')}
                                    className={`flex-1 py-2 text-sm font-medium rounded-lg border transition-colors ${refundType === 'partial'
                                        ? 'bg-white border-orange-400 text-orange-700 shadow-sm'
                                        : 'border-gray-200 text-gray-600 hover:border-orange-200'}`}
                                >
                                    Partial
                                </button>
                            </div>

                            {refundType === 'partial' && (
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Amount (max {formatCurrency(remainingAmount)})
                                    </label>
                                    <div className="relative">
                                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm">$</span>
                                        <input
                                            type="text"
                                            inputMode="decimal"
                                            value={customAmount}
                                            onChange={(e) => handleRefundAmountChange(e.target.value)}
                                            placeholder="0.00"
                                            className="w-full pl-7 pr-3 py-2.5 border border-gray-300 rounded-lg text-base sm:text-sm focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-transparent"
                                        />
                                    </div>
                                    {customAmount && parseFloat(customAmount) > remainingAmount && (
                                        <p className="mt-1 text-xs text-red-600">
                                            Amount exceeds the remaining balance of {formatCurrency(remainingAmount)}.
                                        </p>
                                    )}
                                </div>
                            )}

                            {/* Reason — Stripe standard values */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Reason <span className="text-gray-400 font-normal">(optional)</span>
                                </label>
                                <select
                                    value={reason}
                                    onChange={(e) => setReason(e.target.value)}
                                    className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-base sm:text-sm bg-white focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-transparent"
                                >
                                    <option value="">No reason</option>
                                    <option value="requested_by_customer">Customer request</option>
                                    <option value="duplicate">Duplicate</option>
                                    <option value="fraudulent">Fraud</option>
                                </select>
                            </div>

                            {/* API error */}
                            {refundError && (
                                <div className="flex items-start gap-2 text-red-700 bg-red-50 border border-red-200 rounded-lg p-3">
                                    <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
                                    <p className="text-sm">
                                        {(refundError as any)?.response?.data?.message ?? 'Refund failed. Please try again.'}
                                    </p>
                                </div>
                            )}

                            <div className="flex flex-col-reverse gap-2 pt-1 sm:flex-row">
                                <button
                                    onClick={() => submitRefund()}
                                    disabled={isRefunding || !partialAmountValid}
                                    className="w-full py-2.5 text-sm font-medium bg-orange-500 text-white rounded-lg hover:bg-orange-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors sm:flex-1"
                                >
                                    {isRefunding ? 'Processing…' : 'Confirm Refund'}
                                </button>
                                <button
                                    onClick={handleRefundFormClose}
                                    disabled={isRefunding}
                                    className="w-full px-4 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors sm:w-auto"
                                >
                                    Cancel
                                </button>
                            </div>
                        </div>
                    )}

                    {/* Secondary details — compact grid */}
                    {(payment.card_brand || payment.payment_method_type || payment.processed_by_name || payment.notes) && (
                        <div className="rounded-lg border border-gray-100 divide-y divide-gray-100 text-sm">
                            {(payment.card_brand || payment.payment_method_type) && (
                                <div className="flex items-center gap-3 px-3 py-2.5">
                                    <CreditCard className="h-4 w-4 text-gray-400 shrink-0" />
                                    <div className="min-w-0">
                                        <p className="text-xs text-gray-500">Payment Method</p>
                                        <p className="text-gray-900 font-medium truncate">
                                            {payment.card_brand
                                                ? `${payment.card_brand.charAt(0).toUpperCase() + payment.card_brand.slice(1)} •••• ${payment.card_last4}`
                                                : payment.payment_method_type?.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())}
                                        </p>
                                    </div>
                                </div>
                            )}
                            {payment.processed_by_name && (
                                <div className="flex items-center gap-3 px-3 py-2.5">
                                    <User className="h-4 w-4 text-gray-400 shrink-0" />
                                    <div className="min-w-0">
                                        <p className="text-xs text-gray-500">Processed by</p>
                                        <p className="text-gray-900 font-medium truncate">{payment.processed_by_name}</p>
                                    </div>
                                </div>
                            )}
                            {payment.notes && (
                                <div className="flex items-start gap-3 px-3 py-2.5">
                                    <FileText className="h-4 w-4 text-gray-400 shrink-0 mt-0.5" />
                                    <div className="min-w-0">
                                        <p className="text-xs text-gray-500">Notes</p>
                                        <p className="text-gray-900 whitespace-pre-wrap">{payment.notes}</p>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Stripe IDs */}
                    {(payment.stripe_payment_intent_id || payment.stripe_charge_id) && (
                        <div className="bg-gray-50 rounded-lg p-3 sm:p-4 space-y-3 text-sm">
                            {payment.stripe_payment_intent_id && (
                                <div className="flex flex-col gap-1 sm:flex-row sm:items-start sm:justify-between sm:gap-3">
                                    <span className="shrink-0 text-gray-500">Payment Intent</span>
                                    <code className="break-all text-xs bg-gray-100 px-2 py-1 rounded font-mono text-gray-700 sm:text-right">
                                        {payment.stripe_payment_intent_id}
                                    </code>
                                </div>
                            )}
                            {payment.stripe_charge_id && (
                                <div className="flex flex-col gap-1 sm:flex-row sm:items-start sm:justify-between sm:gap-3">
                                    <span className="shrink-0 text-gray-500">Charge ID</span>
                                    <code className="break-all text-xs bg-gray-100 px-2 py-1 rounded font-mono text-gray-700 sm:text-right">
                                        {payment.stripe_charge_id}
                                    </code>
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* Footer — compact row on mobile */}
                <div className="shrink-0 border-t border-gray-200 bg-gray-50/80 px-4 py-3 pb-[max(0.75rem,var(--safe-area-inset-bottom))] sm:bg-white sm:p-6 keyboard-aware-footer">
                    <div className="flex gap-2">
                        {canRefund(payment.status) && remainingAmount > 0 && !showRefundForm && (
                            <button
                                id="refund-payment-btn"
                                onClick={() => { setRefundSuccess(false); setShowRefundForm(true); }}
                                disabled={!isStripeReady}
                                title={!isStripeReady ? 'Stripe account is not ready — onboarding may be incomplete' : undefined}
                                className="inline-flex flex-1 items-center justify-center gap-2 px-3 py-2.5 text-sm font-medium text-orange-700 bg-orange-100 border border-orange-200 rounded-lg hover:bg-orange-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors sm:flex-none sm:px-4"
                            >
                                <RotateCcw className="h-4 w-4" />
                                Refund
                            </button>
                        )}
                        <button
                            onClick={layer.onClose}
                            className="flex-1 px-3 py-2.5 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors sm:flex-none sm:px-4"
                        >
                            Close
                        </button>
                    </div>
                    {stripePaymentUrl && (
                        <a
                            href={stripePaymentUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="mt-2 inline-flex w-full items-center justify-center gap-1.5 py-1.5 text-xs font-medium text-primary-600 hover:text-primary-700 transition-colors sm:mt-3 sm:text-sm"
                        >
                            <ExternalLink className="h-3.5 w-3.5" />
                            View in Stripe
                        </a>
                    )}
                </div>
            </div>
        </div>
        </LayerDepthContext.Provider>,
        document.body
    );
}
